import {Box, Button, SvgIcon, Typography} from "@mui/material";
import {AddLocationAltOutlined, PlaceOutlined, WhereToVoteOutlined} from "@mui/icons-material";
import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledListItem, StyledSpan} from "@/shared/StyledComponents";
import {BaseApp} from "@/shared/BaseApp";
import {ClaimCodeInput} from "./ClaimCodeInput";
import {DW_LG, PAGE_FRAGMENT_WIDTH, PD_LG, PD_MD} from "@/shared/dimens";
import {
  AccountProfileContextTrait,
  AccountTypeName,
  Page,
  PageClaim,
  PageClaims,
  PageLink,
  PageLinks,
  Pages,
  PageTypeName,
  PRACTICE_PAGE_PROFILE_ROLES_KTM,
  PRACTICE_PAGE_PROFILE_SUPPORTED_ROLES,
  PracticePageProfile,
  PracticePageProfileRole,
  QuickOnboardingStep
} from "@/libwyze-shared/types";
import {FormGenMultiStepHelper} from "@/shared/FormGenMultiStepHelper";
import {AccountProfile, Action} from "@/shared/types";
import {QuickOnboardingStepCompleted} from "./types";
import {appTypeObject} from "@/libwyze-common/types";

export class SetupPracticePageProfileHelper {

  // TODO This should work for Staff/owners too
  static setup(linkAdditional?: boolean): void {
    if (!linkAdditional && QuickOnboardingStepCompleted(QuickOnboardingStep.QUICK_SETUP_PRACTICE)) {
      return;
    }
    const trait = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>();
    const accountProfile = trait.getAccountProfile();
    if (accountProfile.type !== AccountTypeName.PROVIDER) {
      return;
    }
    const providerPageId = trait.getProviderPageId();
    if (!providerPageId) {
      return;
    }
    const id = BaseApp.CONTEXT.showDialog(null, () => <SetupPracticePageProfileView
      onProfileActionClicked={(action: UnlinkedProfileAction) => {
        BaseApp.CONTEXT.hideDialog(id);
        setTimeout(() => {
          switch (action.id) {
            case "claim": {
              BaseApp.CONTEXT.showCustomDialog("Claim an Existing Practice", <ClaimCodeInput
                onVerify={async (code) => {
                  const page = await Pages.getInstance().loadPageByClaimCode(code);
                  if (!page || page.type !== PageTypeName.PRACTICE) {
                    return false;
                  }
                  await PageClaims.getInstance().addPageClaim(PageClaim.createNewEditor(page.id, page.type, accountProfile.id));
                  await PageLinks.getInstance().addPageLink(PageLink.createNew(page.id, providerPageId));
                  window.location.reload();
                  return true;
                }}/>, null, null, null, DW_LG);
              break;
            }
            case "create": {
              const id = BaseApp.CONTEXT.showDialog(null, () => <PracticePageProfileRolesView
                onConfirmClicked={(roles: PracticePageProfileRole[]) => {
                  BaseApp.CONTEXT.hideDialog(id);
                  setTimeout(() => this.showCreateWizard(accountProfile, providerPageId, roles), 100);
                }}/>);
              break;
            }
          }
        }, 100);
      }}
    />);
  }

  static editRoles(page: Page): void {
    if (!page.practicePageProfile) {
      return;
    }
    const id = BaseApp.CONTEXT.showDialog(null, () => <PracticePageProfileRolesView
      selectedRoles={page.practicePageProfile.roles}
      onConfirmClicked={(roles: PracticePageProfileRole[]) => {
        BaseApp.CONTEXT.hideDialog(id);
        page.practicePageProfile.roles = roles;
        Pages.getInstance().addPage(page)
          .then(() => window.location.reload());
      }}/>);
  }

  private static showCreateWizard(accountProfile: AccountProfile, providerPageId: string, roles: PracticePageProfileRole[]) {
    const page = Page.createNewPracticePage(roles);
    FormGenMultiStepHelper.showDialog(page.practicePageProfile, {
      title: "New profile",
      finishAction: new Action("Finish", () => {
        BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
          await Pages.getInstance().addPage(page);
          await PageClaims.getInstance().addPageClaim(PageClaim.createNewEditor(page.id, page.type, accountProfile.id));
          await PageLinks.getInstance().addPageLink(PageLink.createNew(page.id, providerPageId));
          window.location.reload();
        });
      }),
      steps: PracticePageProfile.multiStepConfigSteps(true),
    }, true);
  }
}

type UnlinkedProfileAction = {
  id: string,
  title: string,
  text: string,
  iconType: typeof SvgIcon,
}

const UNLINKED_PROFILE_ACTIONS: UnlinkedProfileAction[] = [
  {
    id: "claim",
    title: "Claim an existing practice",
    text: "Use the 9-digit code given to you by an agent.",
    iconType: WhereToVoteOutlined,
  },
  {
    id: "create",
    title: "Create a new practice",
    text: "Set up a new page with your practice's details.",
    iconType: AddLocationAltOutlined,
  },
  {
    id: "join",
    title: "Join an existing practice",
    text: "Join a practice whose page already exists.",
    iconType: PlaceOutlined,
  },
];

function SetupPracticePageProfileView({onProfileActionClicked}: {
  onProfileActionClicked: (action: UnlinkedProfileAction) => void
}): ReactElement {
  return <StyledBoxColumn
    style={{display: "flex", flexDirection: "column", width: "84dvw", padding: PD_LG, maxWidth: PAGE_FRAGMENT_WIDTH}}>
    {UNLINKED_PROFILE_ACTIONS.map(action => <StyledListItem
      onClick={() => onProfileActionClicked(action)}
      title={action.title}
      text={action.text}
      textWrap={true}
      icon={action.iconType}/>)}
  </StyledBoxColumn>;
}

function PracticePageProfileRolesView({selectedRoles, onConfirmClicked}: {
  selectedRoles?: PracticePageProfileRole[],
  onConfirmClicked: (roles: PracticePageProfileRole[]) => void
}): ReactElement {
  const [roles, setRoles] = React.useState<PracticePageProfileRole[]>(selectedRoles || []);
  return <StyledBoxColumn style={{width: "84dvw", padding: PD_MD, maxWidth: PAGE_FRAGMENT_WIDTH}}>
    <Typography variant={"h5"}>Setup Practice</Typography>
    <Typography variant={"body2"}>Tell us a bit about what kind of practice you're creating a page for. Your choices do
      not affect how the page will appear publicly. You can always adjust these later. Select all that
      apply.</Typography>
    {appTypeObject(PRACTICE_PAGE_PROFILE_SUPPORTED_ROLES).map(role => {
      const metadata = PRACTICE_PAGE_PROFILE_ROLES_KTM.findMetadata(role);
      return <StyledListItem
        selected={roles.includes(role)}
        onClick={() => {
          const index = roles.indexOf(role);
          if (index >= 0) {
            setRoles([...roles.slice(0, index), ...roles.slice(index + 1)]);
          } else {
            setRoles([...roles, role]);
          }
        }}
        title={metadata.text}
        text={metadata.description}
        textWrap={true}
        icon={metadata.iconType}/>;
    })}
    <StyledBoxRow>
      <StyledSpan/>
      <Button disabled={roles.length < 1} onClick={() => onConfirmClicked(roles)}>Confirm</Button>
    </StyledBoxRow>
  </StyledBoxColumn>;
}